<template>
  <g
    :transform="'translate(0, ' + transformYOffset + ')'"
    pointer-events="none"
  >
    <!-- diagonal lines for chart -->
    <pattern id="pattern1"
      x="0"
      y="0"
      width="250"
      :height="pipeEditorHeight"
      patternUnits="userSpaceOnUse"
    >
      <line v-for="index in 60"
        :x1 = "index * 10"
        :y1 = "0"
        :x2 = "(index * 10) - 270"
        :y2 = "pipeEditorHeight"
        stroke="#738f6d"
        opacity=".3"
        stroke-width="2px"
      />
    </pattern>
    <polygon
      :points="getPolygonPoints"
      fill="green"
      fill-opacity=".05"
    />
    <CompositeSprinkler v-for="(index) in getNumOfSprinklers"
      :key="index"
      :num="index"
      :id="'sprinkler' + index"
      class="editor-sprinkler"
      :sprinklers="getNumOfSprinklers"
    />
    <CompositeLine v-for="(line, index) in Lines" :key="index" :line="line" :num="index"/>
    <InletHandle
      :cx = "getInletPositionX"
      :cy = "getInletPositionY"
      :mousedown="handleMouseDown"
    />
  </g>
</template>

<script>
import CompositeLine from '@/components/layout/pipe_editor/pipe_editor_components/CompositeLine';
import { useSegmentStore } from '@/store/SegmentStore';
import CompositeSprinkler from '@/components/layout/pipe_editor/pipe_editor_components/CompositeSprinkler';
import { useInputStore } from '@/store/InputStore';
import InletHandle from '@/components/layout/pipe_editor/pipe_editor_components/InletHandle';

export default {
  name: 'Composite',
  data () {
    return {
      lines: []
    };
  },
  components: {
    InletHandle,
    CompositeLine,
    CompositeSprinkler
  },
  watch: {
    pipeSegments: {
      handler () {
        this.calculateCompositeLines();
      },
      deep: true
    },
    slopeSegments: {
      handler () {
        this.calculateCompositeLines();
      },
      deep: true
    },
    offsets: {
      handler () {
        this.calculateCompositeLines();
      },
      deep: true
    },
    sprinklerSpacing: {
      handler () {
        this.calculateCompositeLines();
      },
      deep: true
    },
    slopeStart: {
      handler () {
        this.calculateCompositeLines();
      },
    },
    pipeEditorHeight () {
      this.calculateCompositeLines();
    },
  },
  computed: {
    Lines () {
      return this.lines;
    },
    pipeEditorHeight () {
      return useSegmentStore().getPipeEditorHeight;
    },
    pipeSegments () {
      return useSegmentStore().getSegments('pipe');
    },
    slopeSegments () {
      return useSegmentStore().getSegments('slope');
    },
    slopeStart () {
      return useSegmentStore().getSlopeStart;
    },
    offsets () {
      return useInputStore().getOffsets;
    },
    sprinklerSpacing () {
      return useInputStore().getSprinklerSpacing;
    },
    transformYOffset () {
      return this.pipeEditorHeight * .5;
    },
    getSlopeRatio () {
      return useSegmentStore().getSlopeRatio;
    },
    getMagScale () {
      return useSegmentStore().getMagScale;
    },
    getNumOfSprinklers () {
      return useSegmentStore().getSprinklerCount + 1;
    },
    getInletPositionX () {
      return useSegmentStore().getInletPosition * this.getMagScale;
    },
    getInletPositionY () {
      const segmentStore = useSegmentStore();
      // find which composite line holds the inlet
      let inletPosition = segmentStore.getInletPosition;
      let selectedLine;
      let lineIndex = 0;
      for (let a = 0; a < this.lines.length; a++) {
        let line = this.lines[a];
        if (inletPosition >= line.x1 && inletPosition < line.x2) {
          selectedLine = line;
          lineIndex = a;
          break;
        }
      }
      if (selectedLine === null || selectedLine === undefined) { return 0; }
      let previousLine =  this.lines[lineIndex-1];
      if (previousLine === undefined) {
        previousLine = { y1: this.slopeStart, y2:0, x1:0, x2:0 };
      }
      let percentageX = (inletPosition - previousLine.x2) / (selectedLine.x2 - selectedLine.x1);
      let valueY = (selectedLine.y2 - selectedLine.y1) * percentageX;
      valueY += previousLine.y2;
      segmentStore.setInletPositionY(valueY);
      return valueY;
    },
    getPolygonPoints () {
      let points = '';
      for (let a = 0; a < this.lines.length; a++) {
        let line = this.lines[a];
        if (a === 0) {
          points += line.x1 * this.getMagScale + ',' + line.y1 + ' ';
        }
        points += line.x2 * this.getMagScale + ',' + line.y2 + ' ';
      }
      if (this.lines.length < 2) { return; }
      let firstPoint = this.lines[0];
      let lastPoint = this.lines[this.lines.length - 1];
      const slopeLayoutSegmentHeight = 30;
      points += lastPoint.x2 * this.getMagScale + ',' + ((this.pipeEditorHeight * .5) - slopeLayoutSegmentHeight) + ' ';
      points += firstPoint.x1 * this.getMagScale + ',' + ((this.pipeEditorHeight * .5) - slopeLayoutSegmentHeight);
      return points;
    },
    getWidth () {
      return useInputStore().getPressureFlowChartWidth - (useSegmentStore().LRBuffer * 2);
    },
    getYScale () {
      return 1;
    }
  },
  mounted () {
    this.calculateCompositeLines();
  },
  methods: {
    handleMouseDown (e) {
      useSegmentStore().getInletComponent.handleMouseDown(e);
    },
    compare ( a, b, value ) {
      value = 'x1';
      if (a[value] < b[value]) { return -1; }
      if (a[value] > b[value]) { return 1; }
      return 0;
    },
    calculateCompositeLines () {
      const segmentStore = useSegmentStore();
      this.lines = [];
      this.slopes = [];
      const rawPipes = segmentStore.getSegments('pipe');
      const rawSlopes = segmentStore.getSegments('slope');
      let cumulativePipeX = 0;
      const pipes = rawPipes.map((p) => {
        const start = cumulativePipeX;
        const end = cumulativePipeX + p.value;
        cumulativePipeX = end;
        return { ...p, startPosition: start, endPosition: end };
      });
      let cumulativeSlopeX = 0;
      const slopeSections = [];
      for (const slope of rawSlopes) {
        const slopeStart = cumulativeSlopeX;
        const slopeEnd = slopeStart + slope.value;
        const splitPoints = pipes
          .map((p) => [p.startPosition, p.endPosition])
          .flat()
          .filter((x) => slopeStart < x && x < slopeEnd)
          .sort((a, b) => a - b);
        const points = [slopeStart, ...splitPoints, slopeEnd];
        for (let i = 0; i < points.length - 1; i++) {
          const x1 = points[i];
          const x2 = points[i + 1];
          const length = x2 - x1;
          const rise = (slope.slope / 100) * length;
          const pipe = pipes.find((p) => p.startPosition <= x1 && p.endPosition >= x2);
          slopeSections.push({
            x1,
            x2,
            length,
            rise,
            slope: slope.slope,
            pipeId: pipe?.id ?? null,
            color: pipe?.color ?? '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0')
          });
        }
        cumulativeSlopeX = slopeEnd;
      }
      let currentY = segmentStore.getSlopeStart ?? 0;
      const rawLines = slopeSections.map((line) => {
        const y1 = currentY;
        const y2 = y1 + line.rise;
        currentY = y2;
        return { ...line, y1, y2 };
      });
      const offsetToZero = rawLines.length > 0 ? rawLines[0].y1 : 0;
      const maxRatio = segmentStore.getMaxRatio * this.getMagScale || 1;
      this.lines = rawLines.map(line => ({
        ...line,
        y1: -(line.y1 - offsetToZero) * maxRatio,
        y2: -(line.y2 - offsetToZero) * maxRatio
      }));
    },
    getSlopeValue (slope, value) {
      return slope * value / 100 * this.getMagScale;
    },
    findNextColor (num) {
      for (let a = num; a < this.lines.length; a++) {
        let line = this.lines[a];
        if (line.type === 'pipe') { return line.color; }
      }
    },
    findNextPipeId (num) {
      for (let a = num; a < this.lines.length; a++) {
        let line = this.lines[a];
        if (line.type === 'pipe') { return line.pipeId; }
      }
    }
  }
};
</script>